module ProcessList {
    "use strict";
    export class ProcessCountsService {
        private initialDataPromise: ng.IPromise<IProcessExecutionCountData>
        private latestUpdateData: IProcessExecutionCountData = {};
        public promiseIsReady: boolean = false;
        static $inject = ["ProcessDataService", "$q", "$rootScope"];
        constructor(private processDataService: ProcessDataService, private $q: ng.IQService, $rootScope: ng.IRootScopeService) {
            $rootScope.$on("processNotification.countsUpdated.frends", (event, data: IProcessExecutionCountEventData) => {
                if (data.isFullRefresh) {
                    this.latestUpdateData = data.executionCounts;
                    this.promiseIsReady = true;
                    this.initialDataPromise = this.$q.resolve(data.executionCounts);
                } else {
                    this.latestUpdateData = this.applyCountUpdates(this.latestUpdateData, data.executionCounts);
                }
                $rootScope.$emit("processList.countsUpdated.frends");
            });
        }

        public getInitialProcessCounts(timeLimit) {
            let deferredInitialData = this.$q.defer();
            this.processDataService.getProcessCounts(timeLimit).then(response => {
                if (response.success) {
                    this.promiseIsReady = true;
                    deferredInitialData.resolve(response.data.executionCounts);
                } else {
                    deferredInitialData.reject(response.errorMessage);
                }
            }).catch(error => {
                deferredInitialData.reject(error);
            });

            this.initialDataPromise = deferredInitialData.promise;
        }

        private applyCountUpdates(countsToUpdate: IProcessExecutionCountData, newCounts: IProcessExecutionCountData) {
            for (var envKey of Object.keys(newCounts)) {
                var newEnvData = newCounts[envKey];
                if (!countsToUpdate[envKey]) {
                    countsToUpdate[envKey] = {};
                }

                for (var procKey of Object.keys(newEnvData)) {
                    var newProcData = newEnvData[procKey];
                    if (!countsToUpdate[envKey][procKey]) {
                        countsToUpdate[envKey][procKey] = {};
                    }

                    for (var dateKey of Object.keys(newProcData)) {
                        // overwrite with the latest
                        countsToUpdate[envKey][procKey][dateKey] = newProcData[dateKey];
                    }
                }
            }

            return countsToUpdate;
        }

        public getLatestExecutionTimeForProcess(environmentName: string, agentGroupName: string, processGuid: string): ng.IPromise<string> {
            return this.getLatestProcessCountsFor(environmentName, agentGroupName, processGuid).then(latestEntry => {
                return latestEntry && this.getLatestStartTimestampFrom(latestEntry);
            });
        }

        public getCurrentExecutionCountForProcess(environmentName: string, agentGroupName: string, processGuid: string): ng.IPromise<number> {
            return this.getLatestProcessData().then(data => {
                var counts = this.groupProcessCounts(environmentName, agentGroupName, data);
                return counts[processGuid] && this.getTotalExecutionCount(counts[processGuid]) || 0;
            });
        }

        /**
         * Gets the latest entry from latestData
         * @param environmentName
         * @param agentGroupName
         * @param processGuid
         */
        private getLatestProcessCountsFor(environmentName: string, agentGroupName: string, processGuid: string): ng.IPromise<IProcessExecutionCount> {
            return this.getLatestProcessData().then(data => {
                const datesWithExecutions = data && data[environmentName] && data[environmentName][processGuid]
                if (!datesWithExecutions) {
                    return null;
                }

                const sortedDateKeys = _.keys(datesWithExecutions).sort();
                if (sortedDateKeys.length < 1) {
                    return null;
                }
                const maxDateKey = _.last(sortedDateKeys);
                return datesWithExecutions[maxDateKey];
            });
        }

        public groupProcessCounts(
            environmentName: string,
            agentGroupName: string,
            data: IProcessExecutionCountData,
            timelimitDays?: number)
            : IGroupedProcessExecutionCounts {

            if (!data || !data[environmentName]) {
                return {};
            }

            let minDateString = "2000-01-01"
            if (timelimitDays) {
                minDateString = moment().add(-1 - timelimitDays, "Days").format("YYYY-MM-DD");
            }

            return _.mapValues(data[environmentName], (processData, processGuid: string) => {
                var dataForProcess = _.toPairs(data[environmentName][processGuid]).filter((v) => v[0] >= minDateString).sort((a, b) => a[0].localeCompare(b[0]));
                var emptyExecutionCounts = this.getEmptyProcessExecutionCount();
                return _.reduce(dataForProcess.map((v) => v[1]), (memo: IProcessExecutionCount, elem: IProcessExecutionCount) => {
                    memo.erroredNotAcknowledged = memo.erroredNotAcknowledged + (elem.erroredNotAcknowledged || 0);

                    memo.running = memo.running + (elem.running || 0);
                    if (elem.latestRunning) {
                        memo.latestRunning = elem.latestRunning;
                    }

                    memo.failed = memo.failed + (elem.failed || 0);
                    if (elem.latestFailed) {
                        memo.latestFailed = elem.latestFailed;
                    }

                    memo.successful = memo.successful + (elem.successful || 0);
                    if (elem.latestSuccessful) {
                        memo.latestSuccessful = elem.latestSuccessful;
                    }

                    if (elem.latestExecutionState) {
                        memo.latestExecutionState = elem.latestExecutionState;
                    }
                    var latestStartTime = this.getLatestStartTimestampFrom(elem);
                    if (latestStartTime) {
                        memo.latestExecutionStartTime = latestStartTime;
                    }

                    memo.total = this.getTotalExecutionCount(memo);

                    return memo;
                }, emptyExecutionCounts);
            });
        }

        private getLatestStartTimestampFrom(elem: IProcessExecutionCount) {
            return _.max([elem.latestSuccessful, elem.latestFailed, elem.latestRunning]);
        }

        private getTotalExecutionCount(elem: IProcessExecutionCount) {
            return elem.running + elem.failed + elem.successful;
        }

        public getProcessCounts(environmentName: string, agentGroupName: string, timelimitDays: number): ng.IPromise<IGroupedProcessExecutionCounts> {
            return this.getLatestProcessData().then(data => {
                let groupedCounts = this.groupProcessCounts(environmentName, agentGroupName, data, timelimitDays);
                return groupedCounts;
            });
        }

        private getEmptyProcessExecutionCount(): IProcessExecutionCount {
            return {
                running: 0,
                failed: 0,
                erroredNotAcknowledged: 0,
                successful: 0,
                total: 0
            };
        }

        private getLatestProcessData(): ng.IPromise<IProcessExecutionCountData> {
            if (!this.initialDataPromise) {
                return this.$q.resolve(null);
            }
            return this.initialDataPromise.then(initialData => {
                if (this.latestUpdateData) {
                    let data = JSON.parse(JSON.stringify(initialData)); // clone - this is faster than _.cloneDeep
                    return this.applyCountUpdates(data, this.latestUpdateData);
                }
                return initialData;
            });
        }
    }

    module.service("ProcessCountsService", ProcessCountsService);
}